<template>
  <div
    class="auth-form-container w-full max-w-[400px] mx-auto"
    :aria-label="STRINGS.aria.labels.resetPasswordForm"
  >
    <!-- Token Error State -->
    <template v-if="tokenError">
      <div
        class="success-panel flex flex-col items-center gap-6 bg-arena-surface/40 rounded-2xl p-8"
      >
        <div class="error-icon">
          <i class="pi pi-times-circle text-5xl text-error-500" aria-hidden="true"></i>
        </div>

        <h2
          class="font-display text-2xl font-extrabold text-arena-text-bright tracking-[0.06em] text-center m-0"
        >
          {{ STRINGS.auth.passwordReset.tokenErrorHeading }}
        </h2>

        <PrimeButton
          @click="emit('navigate', 'forgot-password')"
          data-testid="reset-request-new-link"
          class="min-h-12 touch-manipulation"
        >
          {{ STRINGS.auth.links.requestNewLink }}
        </PrimeButton>

        <button
          class="text-arena-text-muted hover:text-arena-text-bright bg-transparent border-none cursor-pointer text-sm p-0"
          @click="emit('navigate', 'login')"
          data-testid="reset-error-back-to-login"
        >
          {{ STRINGS.auth.links.backToLogin }}
        </button>
      </div>
    </template>

    <!-- Success State -->
    <template v-else-if="resetSuccess">
      <div
        class="success-panel flex flex-col items-center gap-6 bg-arena-surface/40 rounded-2xl p-8"
      >
        <div class="check-icon">
          <i class="pi pi-check-circle text-5xl text-energy-primary" aria-hidden="true"></i>
        </div>

        <h2
          class="font-display text-2xl font-extrabold text-arena-text-bright tracking-[0.06em] text-center m-0"
        >
          {{ STRINGS.auth.passwordReset.resetSuccessHeading }}
        </h2>

        <PrimeButton
          @click="emit('navigate', 'login')"
          data-testid="reset-success-login-button"
          class="min-h-12 touch-manipulation"
        >
          {{ STRINGS.auth.buttons.logIn }}
        </PrimeButton>
      </div>
    </template>

    <!-- Reset Form -->
    <template v-else>
      <h2
        class="auth-heading font-display text-2xl font-extrabold text-arena-text-bright tracking-[0.06em] text-center mb-6"
      >
        {{ STRINGS.auth.passwordReset.resetFormHeading }}
      </h2>

      <form
        class="auth-card flex flex-col gap-4 p-6 rounded-xl border border-arena-border bg-arena-surface/80 backdrop-blur-sm transition-colors duration-150"
        :class="
          formFocused
            ? 'border-energy-primary/60 bg-arena-surface/80'
            : 'border-energy-primary/30 bg-arena-surface/80'
        "
        @submit.prevent="handleSubmit"
        @focusin="formFocused = true"
        @focusout="formFocused = false"
        autocomplete="on"
        novalidate
      >
        <!-- New Password -->
        <div class="flex flex-col gap-1.5">
          <label for="reset-new-password" class="text-sm font-semibold text-arena-text-bright">
            {{ STRINGS.auth.labels.newPassword }}
          </label>
          <div class="relative">
            <InputText
              id="reset-new-password"
              ref="passwordInput"
              v-model="newPassword"
              name="new-password"
              :type="showPassword ? 'text' : 'password'"
              class="auth-input min-h-12 touch-manipulation w-full pr-10"
              :placeholder="STRINGS.auth.placeholders.newPassword"
              autocomplete="new-password"
              :aria-invalid="!!displayError"
              data-testid="reset-new-password-input"
              @keyup.enter="handleSubmit"
            />
            <button
              type="button"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-arena-text-muted hover:text-arena-text-bright transition-colors p-1"
              :aria-label="STRINGS.aria.labels.togglePasswordVisibility"
              :aria-pressed="showPassword"
              @click="showPassword = !showPassword"
            >
              <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'" aria-hidden="true"></i>
            </button>
          </div>
          <span class="text-xs text-arena-text-muted">{{ STRINGS.auth.hints.password }}</span>
        </div>

        <!-- Confirm Password -->
        <div class="flex flex-col gap-1.5">
          <label for="reset-confirm-password" class="text-sm font-semibold text-arena-text-bright">
            {{ STRINGS.auth.labels.confirmPassword }}
          </label>
          <div class="relative">
            <InputText
              id="reset-confirm-password"
              v-model="confirmPassword"
              name="confirm-password"
              :type="showConfirmPassword ? 'text' : 'password'"
              class="auth-input min-h-12 touch-manipulation w-full pr-10"
              :placeholder="STRINGS.auth.placeholders.confirmPassword"
              autocomplete="new-password"
              :aria-invalid="!!displayError"
              data-testid="reset-confirm-password-input"
              @keyup.enter="handleSubmit"
            />
            <button
              type="button"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-arena-text-muted hover:text-arena-text-bright transition-colors p-1"
              :aria-label="STRINGS.aria.labels.togglePasswordVisibility"
              :aria-pressed="showConfirmPassword"
              @click="showConfirmPassword = !showConfirmPassword"
            >
              <i
                :class="showConfirmPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"
                aria-hidden="true"
              ></i>
            </button>
          </div>
        </div>

        <!-- Error display -->
        <p
          v-if="displayError"
          ref="errorRef"
          class="text-sm text-error-500 text-center m-0"
          role="alert"
          aria-live="polite"
          tabindex="-1"
        >
          {{ displayError }}
        </p>

        <!-- Submit button -->
        <PrimeButton
          :disabled="!canSubmit"
          data-testid="reset-submit-button"
          class="min-h-12 touch-manipulation mt-2"
          :full-width="true"
          @click="handleSubmit"
        >
          <template v-if="passwordReset.isLoading.value">
            {{ STRINGS.auth.buttons.settingPassword }}<LoadingDots />
          </template>
          <template v-else>
            {{ STRINGS.auth.buttons.setNewPassword }}
          </template>
        </PrimeButton>
      </form>

      <!-- Footer links -->
      <div class="auth-footer flex flex-col items-center gap-2 mt-6 text-sm text-arena-text-muted">
        <button
          class="text-arena-text-muted hover:text-arena-text-bright bg-transparent border-none cursor-pointer text-sm p-0"
          @click="emit('navigate', 'login')"
          data-testid="reset-back-to-login-link"
        >
          {{ STRINGS.auth.links.backToLogin }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import InputText from 'primevue/inputtext';
import PrimeButton from './ui/PrimeButton.vue';
import LoadingDots from './ui/LoadingDots.vue';
import { usePasswordReset } from '@/composables/usePasswordReset';
import { isAuthError } from '@/types/auth';
import { AUTH_CONFIG } from '@/config/constants';
import STRINGS from '@/constants/strings';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';

const props = defineProps<{
  token: string;
}>();

const emit = defineEmits<{
  navigate: [target: 'login' | 'forgot-password' | 'homepage'];
}>();

const passwordReset = usePasswordReset();

const newPassword = ref('');
const confirmPassword = ref('');
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const formFocused = ref(false);
const resetSuccess = ref(false);
const tokenError = ref(false);
const localError = ref('');
const passwordInput = ref();
const errorRef = ref<HTMLElement>();

const displayError = computed(() => {
  if (passwordReset.rateLimitCountdown.value > 0) {
    return STRINGS.templates.rateLimitCountdown(passwordReset.rateLimitCountdown.value);
  }
  if (passwordReset.error.value) {
    return passwordReset.error.value.message;
  }
  return localError.value;
});

const canSubmit = computed(() => {
  return (
    newPassword.value.length > 0 &&
    confirmPassword.value.length > 0 &&
    !passwordReset.isLoading.value &&
    passwordReset.rateLimitCountdown.value === 0
  );
});

async function handleSubmit(): Promise<void> {
  if (!canSubmit.value) return;

  localError.value = '';
  passwordReset.clearError();

  if (newPassword.value.length < AUTH_CONFIG.PASSWORD_MIN_LENGTH) {
    localError.value = STRINGS.auth.errors.passwordTooShort;
    focusError();
    return;
  }

  if (newPassword.value !== confirmPassword.value) {
    localError.value = STRINGS.auth.errors.passwordsDoNotMatch;
    focusError();
    return;
  }

  try {
    await passwordReset.confirmReset(props.token, newPassword.value);
    resetSuccess.value = true;
  } catch (err: unknown) {
    if (isAuthError(err) && (err.type === 'invalid_token' || err.type === 'expired_token')) {
      tokenError.value = true;
    } else if (!isAuthError(err)) {
      reportClientError(err, ErrorLocation.AuthResetPasswordUnexpected);
    }
    focusError();
  }
}

function focusError(): void {
  nextTick(() => {
    errorRef.value?.focus();
  });
}

onMounted(() => {
  nextTick(() => {
    const el = passwordInput.value?.$el ?? passwordInput.value;
    const input = el?.querySelector?.('input') ?? el;
    if (input && typeof input.focus === 'function') {
      input.focus();
    }
  });
});
</script>

<style scoped>
.auth-heading {
  animation: homepageEntrance 0.8s ease-out both;
}

.auth-card {
  animation: homepageEntrance 0.8s ease-out 0.2s both;
}

.auth-footer {
  animation: homepageEntrance 0.8s ease-out 0.3s both;
}

.success-panel {
  animation: successEntrance 1s ease-out both;
}

.check-icon {
  animation: checkScale 0.6s ease-out 0.3s both;
}

.error-icon {
  animation: checkScale 0.6s ease-out 0.3s both;
}

@keyframes homepageEntrance {
  from {
    transform: translateY(16px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes successEntrance {
  from {
    transform: translateY(24px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes checkScale {
  from {
    transform: scale(0.3);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.auth-input {
  padding: 0.75rem 1rem;
  border: 2px solid var(--color-arena-border);
  border-radius: var(--radius-input, 0.5rem);
  font-size: 1rem;
  transition: none;
  outline: none;
  background: rgba(26, 22, 18, 0.8);
  color: var(--color-arena-text-bright);
}

.auth-input:focus {
  outline: none;
}

.auth-input::placeholder {
  color: var(--color-arena-text-muted);
}

@media (prefers-reduced-motion: reduce) {
  .auth-heading,
  .auth-card,
  .auth-footer,
  .success-panel,
  .check-icon,
  .error-icon {
    animation: none !important;
  }
}
</style>
